import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Stethoscope, DollarSign, Wallet, Heart, Building } from "lucide-react";

const fmt = (v) => typeof v === "number" ? `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "-";
const pct = (v) => typeof v === "number" ? `${v.toFixed(1)}%` : "-";
const num = (v) => typeof v === "number" ? v.toLocaleString() : "-";

function Row({ label, value, highlight, sub, positive, negative, bold }) {
  return (
    <div className={`flex justify-between py-0.5 ${highlight ? "font-medium" : ""} ${sub ? "text-gray-500 pl-2" : ""} ${bold ? "text-base font-bold pt-1" : ""}`}>
      <span>{label}</span>
      <span className={`tabular-nums ${positive ? "text-emerald-600" : ""} ${negative ? "text-red-500" : ""}`}>{value}</span>
    </div>
  );
}

export function OperationalCard({ ops }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Stethoscope className="w-4 h-4 text-blue-500" />{t("hospital.operations")}</CardTitle></CardHeader>
      <CardContent className="text-sm space-y-1">
        <Row label={t("hospital.totalDemand") || "Demanda Total"} value={num(ops.total_demand)} />
        <Row label={t("hospital.patients")} value={num(ops.patients_treated)} highlight />
        <Row label={t("hospital.demandMet") || "Demanda Cubierta"} value={pct(ops.demand_met_pct)} />
        <div className="border-t pt-1 mt-1" />
        <Row label={t("hospital.consultations") || "Consultas"} value={num(ops.consultations)} />
        <Row label={t("hospital.surgeries") || "Cirugías"} value={num(ops.surgeries)} />
        <Row label={t("hospital.hospitalizations") || "Hospitalizaciones"} value={num(ops.hospitalizations)} sub />
        <Row label={"  " + (t("hospital.wardRate") || "Sala General")} value={num(ops.hosp_general)} sub />
        <Row label={"  " + (t("hospital.bedsPrivate") || "Habitaciones Privadas")} value={num(ops.hosp_private)} sub />
        <Row label={"  " + (t("hospital.bedsICU") || "UCI")} value={num(ops.hosp_icu)} sub />
        <div className="border-t pt-1 mt-1" />
        <Row label={t("hospital.bedOccupancy")} value={pct(ops.bed_occupancy)} />
        <Row label={t("hospital.avgStay") || "Estancia Media (días)"} value={ops.avg_stay_days} />
      </CardContent>
    </Card>
  );
}

export function FinancialCard({ revenue, expenses, netProfit }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-500" />{t("hospital.finance")}</CardTitle></CardHeader>
      <CardContent className="text-sm space-y-1">
        <Row label={t("hospital.totalRevenue") || "Ingresos Totales"} value={fmt(revenue.total)} highlight positive />
        <Row label={"  " + (t("hospital.consultations") || "Consultas")} value={fmt(revenue.consultations)} sub />
        <Row label={"  " + (t("hospital.surgeries") || "Cirugías")} value={fmt(revenue.surgeries)} sub />
        <Row label={"  " + (t("hospital.wardRate") || "Sala General")} value={fmt(revenue.ward)} sub />
        <Row label={"  " + (t("hospital.bedsPrivate") || "Hab. Privadas")} value={fmt(revenue.private_rooms)} sub />
        <Row label={"  " + (t("hospital.bedsICU") || "UCI")} value={fmt(revenue.icu)} sub />
        {revenue.donations > 0 && <Row label={"  " + (t("hospital.donations") || "Donations")} value={fmt(revenue.donations)} sub />}
        <div className="border-t pt-1 mt-1" />
        <Row label={t("hospital.totalExpenses") || "Gastos Totales"} value={fmt(expenses.total)} highlight negative />
        <Row label={"  " + (t("hospital.salaries") || "Salarios")} value={fmt(expenses.salaries)} sub />
        <Row label={"  " + t("hospital.marketing")} value={fmt(expenses.marketing)} sub />
        <Row label={"  " + t("hospital.maintenance")} value={fmt(expenses.maintenance)} sub />
        <Row label={"  " + t("hospital.equipment")} value={fmt(expenses.equipment)} sub />
        <Row label={"  " + t("hospital.quality")} value={fmt((expenses.quality_program || 0) + (expenses.safety || 0) + (expenses.patient_experience || 0))} sub />
        <Row label={"  " + (t("hospital.supplies") || "Suministros")} value={fmt(expenses.supplies)} sub />
        <Row label={"  " + (t("hospital.interest") || "Intereses")} value={fmt(expenses.interest)} sub />
        <Row label={"  " + (t("hospital.otherExpenses") || "Otros")} value={fmt((expenses.hiring || 0) + (expenses.firing || 0) + (expenses.training || 0) + (expenses.bed_purchases || 0) + (expenses.renovation || 0) + (expenses.strategic || 0) + (expenses.nurse_ratio || 0))} sub />
        <div className="border-t pt-1 mt-2" />
        <Row label={t("hospital.netProfit")} value={fmt(netProfit)} highlight positive={netProfit >= 0} negative={netProfit < 0} bold />
      </CardContent>
    </Card>
  );
}

export function BalanceSheetCard({ bs }) {
  const { t } = useTranslation();
  if (bs.cash === undefined) return null;
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Wallet className="w-4 h-4 text-sky-500" />{t("hospital.balanceSheet") || "Balance Sheet"}</CardTitle></CardHeader>
      <CardContent className="text-sm space-y-1">
        <Row label={t("hospital.cash") || "Cash"} value={fmt(bs.cash)} highlight positive={bs.cash >= 0} negative={bs.cash < 0} />
        <div className="border-t pt-1 mt-1" />
        <Row label={t("hospital.shortTermDebt") || "Short-term Debt"} value={fmt(bs.short_term_debt)} negative />
        <Row label={t("hospital.longTermDebt") || "Long-term Debt"} value={fmt(bs.long_term_debt)} negative />
        <div className="border-t pt-1 mt-1" />
        <Row label={t("hospital.totalEquity") || "Total Equity"} value={fmt(bs.total_equity)} highlight positive={bs.total_equity >= 0} negative={bs.total_equity < 0} />
        <Row label={t("hospital.retainedEarnings") || "Retained Earnings"} value={fmt(bs.retained_earnings)} sub positive={bs.retained_earnings >= 0} negative={bs.retained_earnings < 0} />
        <Row label={t("hospital.cumulativeProfit") || "Cumulative Profit"} value={fmt(bs.cumulative_profit)} sub positive={bs.cumulative_profit >= 0} negative={bs.cumulative_profit < 0} />
        <div className="border-t pt-1 mt-2" />
        <Row label={t("hospital.valuation") || "Valuation"} value={fmt(bs.valuation)} highlight bold />
      </CardContent>
    </Card>
  );
}

export function QualityCard({ ratings, decisions }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Heart className="w-4 h-4 text-pink-500" />{t("hospital.quality")}</CardTitle></CardHeader>
      <CardContent className="text-sm space-y-1">
        <Row label={t("hospital.patientSat")} value={`${ratings.patient_satisfaction}/100`} />
        <Row label={t("hospital.safety")} value={`${ratings.safety_rating}/100`} />
        <Row label={t("hospital.reputation")} value={`${ratings.reputation}/100`} />
        <Row label={t("hospital.marketShare")} value={pct(ratings.market_share)} />
        <div className="border-t pt-1 mt-1" />
        <Row label={t("hospital.qualityProgram") || "Programa de Calidad"} value={decisions.quality_program || "none"} />
        <Row label={t("hospital.safetyInvestment") || "Inversión en Seguridad"} value={fmt(decisions.safety_investment)} />
        <Row label={t("hospital.patientExperience") || "Experiencia del Paciente"} value={decisions.patient_experience || "none"} />
      </CardContent>
    </Card>
  );
}

export function StaffInfraCard({ staff, infra, ratings }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Building className="w-4 h-4 text-indigo-500" />{t("hospital.staffAndInfra") || "Personal e Infraestructura"}</CardTitle></CardHeader>
      <CardContent className="text-sm space-y-1">
        <Row label={t("hospital.doctors")} value={staff.doctors} />
        <Row label={t("hospital.nurses")} value={staff.nurses} />
        <Row label={t("hospital.reception")} value={staff.reception} />
        <Row label={t("hospital.totalStaff")} value={staff.total} highlight />
        <Row label={t("hospital.staffSat")} value={`${ratings.staff_satisfaction}/100`} />
        <div className="border-t pt-1 mt-1" />
        <Row label={t("hospital.bedsGeneral")} value={infra.beds_general} />
        <Row label={t("hospital.bedsPrivate")} value={infra.beds_private} />
        <Row label={t("hospital.bedsICU")} value={infra.beds_icu} />
        <Row label={t("hospital.totalBeds")} value={infra.total_beds} highlight />
        <Row label={t("hospital.equipment")} value={infra.equipment_level || "-"} />
        <Row label={t("hospital.departments") || "Departamentos"} value={infra.departments?.length || 0} />
      </CardContent>
    </Card>
  );
}
